import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Brain,
  ShieldCheck,
  Target,
  Lightbulb,
  AlertCircle,
} from "lucide-react";
import type { ContactDetailFull, SegmentData } from "@/hooks/useContactDetail";

// ── Props ──────────────────────────────────────────────────────────

interface ContactIntelTabProps {
  contact: ContactDetailFull;
}

// ── Helpers ────────────────────────────────────────────────────────

function ConfidenceBadge({ level }: { level: string | null | undefined }) {
  if (!level) return null;
  const colors: Record<string, string> = {
    high: "bg-green-500/10 text-green-700 border-green-500/20",
    medium: "bg-amber-500/10 text-amber-700 border-amber-500/20",
    low: "bg-red-500/10 text-red-700 border-red-500/20",
  };
  return (
    <Badge variant="outline" className={`text-[11px] ${colors[level] || ""}`}>
      {level} confidence
    </Badge>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-sm text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────

export function ContactIntelTab({ contact }: ContactIntelTabProps) {
  const sd: SegmentData | null =
    contact.segment_data && typeof contact.segment_data === "object"
      ? (contact.segment_data as SegmentData)
      : null;

  const hasL2 = !!(sd?.buyer_segment || contact.buyer_segment);
  const hasL1 = !!(contact.seniority_normalized || contact.department_normalized);
  const hasCarrFields = !!(contact.company_icp_segment || contact.pain_point_category || contact.messaging_variant_default);

  return (
    <div className="space-y-4 p-1">
      {/* ── Classification Section ─────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
          <Brain className="h-3.5 w-3.5" />
          Classification
        </h3>

        {hasL2 ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {(sd?.buyer_segment || contact.buyer_segment) && (
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  {sd?.buyer_segment || contact.buyer_segment}
                </Badge>
              )}
              {(sd?.decision_role || contact.decision_role) && (
                <Badge variant="outline">
                  {sd?.decision_role || contact.decision_role}
                </Badge>
              )}
              <ConfidenceBadge level={sd?.confidence || contact.segment_confidence} />
              {contact.segment_source && (
                <Badge variant="secondary" className="text-[11px]">
                  {contact.segment_source}
                </Badge>
              )}
            </div>
            {sd?.reasoning && (
              <p className="text-xs text-muted-foreground italic">{sd.reasoning}</p>
            )}
          </div>
        ) : hasL1 ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {contact.seniority_normalized && (
                <Badge variant="outline">{contact.seniority_normalized}</Badge>
              )}
              {contact.department_normalized && (
                <Badge variant="secondary">{contact.department_normalized}</Badge>
              )}
              {contact.decision_role && (
                <Badge variant="outline">{contact.decision_role}</Badge>
              )}
              {contact.is_decision_maker && (
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Decision Maker
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              L1 classification (rule-based). L2 AI classification has not yet been run.
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground py-3">
            <AlertCircle className="h-4 w-4" />
            <p className="text-xs">Not yet classified. Classification will be available after the L2 pipeline runs.</p>
          </div>
        )}
      </section>

      <Separator />

      {/* ── Persona Intelligence (L2) ─────────────────────────── */}
      {hasL2 && sd && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" />
              Persona Intelligence
            </h3>
            <div className="space-y-2">
              {sd.persona_summary && (
                <InfoRow label="Summary" value={sd.persona_summary} />
              )}
              {sd.primary_pain_points && sd.primary_pain_points.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-xs text-muted-foreground w-32 shrink-0">Pain Points</span>
                  <div className="flex flex-wrap gap-1">
                    {sd.primary_pain_points.map((pp, i) => (
                      <Badge key={i} variant="outline" className="text-[11px]">{pp}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <InfoRow label="Messaging Angle" value={sd.messaging_angle} />
              <InfoRow label="Value Hook" value={sd.value_hook} />
              <InfoRow label="Buying Context" value={sd.buying_context} />
              <InfoRow label="SSO Relevance" value={sd.sso_relevance} />
            </div>
          </section>
          <Separator />
        </>
      )}

      {/* ── CARR-Specific Fields ───────────────────────────────── */}
      {hasCarrFields && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" />
              Targeting & Positioning
            </h3>
            <div className="space-y-2">
              <InfoRow label="ICP Segment" value={contact.company_icp_segment} />
              <InfoRow label="Pain Point Category" value={contact.pain_point_category} />
              <InfoRow label="Messaging Variant" value={contact.messaging_variant_default} />
              <InfoRow label="Decision Authority" value={contact.decision_authority} />
              {contact.is_champion && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-32 shrink-0">Champion</span>
                  <Badge className="bg-green-500/10 text-green-700 border-green-500/20 text-[11px]">
                    Identified Champion
                  </Badge>
                </div>
              )}
            </div>
          </section>
          <Separator />
        </>
      )}

    </div>
  );
}
